import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Chip, CircularProgress, Drawer, IconButton, Pagination, TextField, Typography } from "@mui/material";
import { keyframes } from "@emotion/react";
import React, { FC, useEffect, useState } from "react";

import { HelpState, isUnseenNew, isWatched, todayKey } from "../helpStorage";
import { getHelpVideos } from "../services";
import { HelpVideo, HelpVideoList, PlayOptions } from "../types";
import VideoCard from "./VideoCard";

const DRAWER_WIDTH = 420;
const LIBRARY_PAGE_SIZE = 10;
const SEARCH_DELAY_MS = 300;

interface HelpDrawerProps {
  open: boolean;
  /** The current page's videos. */
  videos: HelpVideo[];
  /** Every video the user may see, for the "All videos" link. */
  available: number;
  locale: string;
  state: HelpState;
  onClose: () => void;
  onPlay: (video: HelpVideo, options?: PlayOptions) => void;
}

const SectionTitle: FC<{ children: React.ReactNode; color?: string }> = ({ children, color = "text.secondary" }) => (
  <Typography variant="overline" sx={{ color, mt: 2 }}>
    {children}
  </Typography>
);

const float = keyframes`0%, 100% { transform: translateY(0) } 50% { transform: translateY(-6px) }`;
const record = keyframes`0% { transform: scaleX(0.05) } 70%, 100% { transform: scaleX(1) }`;
const twinkle = keyframes`0%, 100% { opacity: 0.2; transform: scale(0.6) } 50% { opacity: 1; transform: scale(1) }`;
const SPARKLES = [
  { top: 6, left: 18, delay: "0s" },
  { top: 2, left: 128, delay: "0.6s" },
  { top: 70, left: 140, delay: "1.2s" },
];

/** A page with no videos yet: a small "being recorded" scene, still for people who prefer less motion. */
const ComingSoon: FC = () => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 1, py: 3 }}>
    <Box
      aria-hidden
      sx={{
        position: "relative",
        width: 160,
        height: 100,
        mb: 1,
        "& *": { "@media (prefers-reduced-motion: reduce)": { animation: "none !important" } },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: "12px 20px 8px",
          borderRadius: 2,
          border: 2,
          borderColor: "primary.main",
          bgcolor: "background.paper",
          display: "grid",
          placeItems: "center",
          animation: `${float} 3s ease-in-out infinite`,
        }}
      >
        <Box sx={{ width: 34, height: 34, borderRadius: "50%", bgcolor: "primary.main", display: "grid", placeItems: "center" }}>
          <Box sx={{ ml: "3px", width: 0, height: 0, borderTop: "7px solid transparent", borderBottom: "7px solid transparent", borderLeft: "11px solid", borderLeftColor: "primary.contrastText" }} />
        </Box>
        <Box sx={{ position: "absolute", left: 10, right: 10, bottom: 8, height: 4, borderRadius: 2, bgcolor: "action.hover", overflow: "hidden" }}>
          <Box sx={{ height: "100%", bgcolor: "primary.main", transformOrigin: "left", animation: `${record} 2.8s ease-out infinite` }} />
        </Box>
      </Box>
      {SPARKLES.map(({ top, left, delay }) => (
        <Box
          key={`${top}-${left}`}
          sx={{ position: "absolute", top, left, color: "primary.main", fontSize: 14, lineHeight: 1, animation: `${twinkle} 1.8s ease-in-out ${delay} infinite` }}
        >
          ✦
        </Box>
      ))}
    </Box>
    <Typography fontWeight={700}>New guides are on the way</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300 }}>
      We are recording short videos for this page. Meanwhile, explore all videos for quick how-tos.
    </Typography>
  </Box>
);

interface ViewProps {
  state: HelpState;
  play: (video: HelpVideo, start?: number) => void;
}

/** This page's videos: a new unwatched one featured on top, then the rest. */
const PageView: FC<ViewProps & { videos: HelpVideo[] }> = ({ videos, state, play }) => {
  const featured = videos.find((video) => isUnseenNew(video, state, todayKey()));
  const rest = videos.filter((video) => video !== featured);
  if (!videos.length) return <ComingSoon />;
  return (
    <>
      {featured && (
        <>
          <SectionTitle color="primary.dark">New on this page</SectionTitle>
          <VideoCard video={featured} state={state} highlight onPlay={(start) => play(featured, start)} />
        </>
      )}
      {rest.length > 0 && (
        <>
          <SectionTitle>
            On this page ({rest.filter((video) => isWatched(video, state)).length} of {rest.length} watched)
          </SectionTitle>
          {rest.map((video) => (
            <VideoCard key={video.id} video={video} state={state} onPlay={(start) => play(video, start)} />
          ))}
        </>
      )}
    </>
  );
};

/** Every video the user may see, searched and paged on the server, newest first. */
const LibraryView: FC<ViewProps & { locale: string }> = ({ locale, state, play }) => {
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [module, setModule] = useState<string>();
  // Kept across searches and pages so the chips don't flicker while a page loads.
  const [modules, setModules] = useState<string[]>([]);
  const [result, setResult] = useState<HelpVideoList>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(query.trim());
      setPage(1);
    }, SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    let active = true;
    setResult(undefined);
    setFailed(false);
    getHelpVideos({ locale, q: search || undefined, module, limit: LIBRARY_PAGE_SIZE, offset: (page - 1) * LIBRARY_PAGE_SIZE })
      .then((next) => {
        if (!active) return;
        // The list shrank under this page (catalog or permissions changed): go to its new last page.
        const lastPage = Math.max(1, Math.ceil(next.total / LIBRARY_PAGE_SIZE));
        if (!next.videos.length && page > lastPage) {
          setPage(lastPage);
          return;
        }
        setResult(next);
        setModules(next.modules);
      })
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
    };
  }, [locale, search, module, page]);

  const pickModule = (next?: string) => {
    setModule(next);
    setPage(1);
  };

  const pages = result ? Math.ceil(result.total / LIBRARY_PAGE_SIZE) : 0;

  return (
    <>
      <TextField
        size="small"
        placeholder='Search videos, e.g. "follow-up"'
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      {modules.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Chip label="All" size="small" color={module ? "default" : "primary"} variant={module ? "outlined" : "filled"} onClick={() => pickModule(undefined)} />
          {modules.map((name) => (
            <Chip
              key={name}
              label={name}
              size="small"
              color={module === name ? "primary" : "default"}
              variant={module === name ? "filled" : "outlined"}
              onClick={() => pickModule(name)}
            />
          ))}
        </Box>
      )}
      {failed && (
        <Typography variant="body2" color="text.secondary">
          Videos could not be loaded. Please try again later.
        </Typography>
      )}
      {!failed && !result && <CircularProgress size={28} sx={{ alignSelf: "center", my: 3 }} />}
      {result?.videos.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No videos match.
        </Typography>
      )}
      {result?.videos.map((video) => (
        <VideoCard key={video.id} video={video} state={state} onPlay={(start) => play(video, start)} />
      ))}
      {pages > 1 && (
        <Pagination
          count={pages}
          page={page}
          onChange={(_, next) => setPage(next)}
          size="small"
          sx={{ alignSelf: "center", mt: 1 }}
        />
      )}
    </>
  );
};

const HelpDrawer: FC<HelpDrawerProps> = ({ open, videos, available, locale, state, onClose, onPlay }) => {
  const [library, setLibrary] = useState(false);

  // Every opening starts on this page's videos.
  useEffect(() => {
    if (open) setLibrary(false);
  }, [open]);

  const play = (video: HelpVideo, start?: number) => {
    onClose();
    onPlay(video, { start });
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: DRAWER_WIDTH }, p: 2.5, gap: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {library && (
          <IconButton size="small" aria-label="Back to this page's videos" onClick={() => setLibrary(false)}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <Typography variant="h6" sx={{ flex: 1 }}>
          {library ? "All videos" : "Help"}
        </Typography>
        <IconButton size="small" aria-label="Close help" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      {library ? (
        <LibraryView locale={locale} state={state} play={play} />
      ) : (
        <>
          <PageView videos={videos} state={state} play={play} />
          <Button
            variant={videos.length ? "outlined" : "contained"}
            onClick={() => setLibrary(true)}
            sx={{ alignSelf: videos.length ? "flex-start" : "center", textTransform: "none" }}
          >
            All videos ({available})
          </Button>
        </>
      )}
    </Drawer>
  );
};

export default HelpDrawer;
